import { Fragment, useState } from 'react';
import { useProjectProfiles, useProfileActions, useReleases, useAgentProfiles } from '@/hooks/useAgents';
import { useAuth } from '@/context/AuthContext';
import { ProjectProfile, SetupReport, releaseStatus } from '@/api/agents.api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { AlertTriangle, Check, CheckCircle2, ClipboardCheck, Plus, Power, Search, Tag, Wrench, XCircle } from 'lucide-react';
import { apiErrorMessage, formatRelativeTime } from '@/utils/helpers';

/**
 * How each project works, for the manager and the agents (plan UC-01): the base
 * branch, companion repo, CI jobs, review apps and the packages it publishes.
 * "Discover" reads GitLab and proposes values; nothing is used as confirmed
 * until an executive saves it. "Check" (CEO and PM) says whether the project is ready for AI
 * management and adds the missing members (CEO, PM, bots). "Make live" (CEO) lets the manager act on a
 * project instead of only recording, then runs Check. Below: releases made by CI (newPortalModel#41).
 */

const STATE = {
  ok: { icon: CheckCircle2, className: 'text-green-600', label: 'ok' },
  fixed: { icon: Wrench, className: 'text-blue-600', label: 'fixed now' },
  warn: { icon: AlertTriangle, className: 'text-amber-600', label: 'check' },
  missing: { icon: XCircle, className: 'text-red-600', label: 'missing' },
} as const;

function SetupResult({ report }: { report: SetupReport }) {
  const missing = report.checks.filter((c) => c.state === 'missing').length;
  const fixed = report.checks.filter((c) => c.state === 'fixed').length;
  return (
    <div className="space-y-2 rounded border bg-muted/30 p-3">
      <div className={`text-sm font-medium ${report.ready ? 'text-green-700' : 'text-red-700'}`}>
        {report.ready ? 'Ready for AI management' : `Not ready: ${missing} thing${missing === 1 ? '' : 's'} missing`}
        {fixed > 0 && <span className="font-normal text-blue-700"> · fixed {fixed} now</span>}
        <span className="ml-2 text-xs font-normal text-muted-foreground">checked {formatRelativeTime(report.checkedAt)}</span>
      </div>
      <ul className="space-y-1">
        {report.checks.map((c) => {
          const s = STATE[c.state];
          const Icon = s.icon;
          return (
            <li key={c.key} className="flex gap-2 text-xs">
              <Icon className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${s.className}`} aria-label={s.label} />
              <div>
                <span className="font-medium">{c.title}</span>
                <span className="text-muted-foreground"> · {c.detail}</span>
                {c.how && (c.state === 'warn' || c.state === 'missing') && <div className="text-muted-foreground">→ {c.how}</div>}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function ProfileRow({ p, canEdit, canCheck }: { p: ProjectProfile; canEdit: boolean; canCheck: boolean }) {
  const { discover, save, check, live } = useProfileActions();
  const [report, setReport] = useState<SetupReport | null>(null);
  const [confirmLive, setConfirmLive] = useState(false);
  const { data: agentProfiles } = useAgentProfiles();
  const [proposal, setProposal] = useState<ProjectProfile | null>(null);
  const [base, setBase] = useState(p.baseBranch);
  const [agent, setAgent] = useState(p.agentProfile || '');
  const [qa, setQa] = useState(!!p.qa);
  const [aiReview, setAiReview] = useState(!!p.aiReview);
  const [error, setError] = useState<string | null>(null);
  const shown = proposal || p;

  const run = async () => {
    setError(null);
    try {
      const found = await discover.mutateAsync(p.projectPath);
      setProposal({ ...p, ...found });
      setBase(found.baseBranch || p.baseBranch);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const runCheck = async () => {
    setError(null);
    try {
      setReport(await check.mutateAsync(p.projectPath));
    } catch (e) {
      setError((e as Error).message);
    }
  };

  // Going live is the CEO's decision; right after it, Check adds the members and the webhook
  const changeLive = async (on: boolean) => {
    setError(null);
    try {
      await live.mutateAsync({ project: p.projectPath, live: on });
      setConfirmLive(false);
      if (on) await runCheck();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const confirm = async () => {
    setError(null);
    try {
      const { stored: _stored, confirmedAt: _c, confirmedBy: _b, ...rest } = shown;
      await save.mutateAsync({ project: p.projectPath, profile: { ...rest, baseBranch: base, agentProfile: agent || undefined, qa, aiReview: qa && aiReview } });
      setProposal(null);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <Fragment>
      <tr className={`align-top ${report ? '' : 'border-b'}`}>
        <td className="py-2 pr-3">
          <div className="font-mono text-xs">{p.projectPath}</div>
          {p.confirmedAt ? (
            <div className="text-xs text-green-700">confirmed by @{p.confirmedBy} {formatRelativeTime(p.confirmedAt)}</div>
          ) : (
            <div className="text-xs text-amber-700">{p.stored ? 'discovered, not confirmed' : 'guessed from issue MRs'}</div>
          )}
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
            {p.live ? (
              <span className="rounded bg-green-100 px-1.5 py-0.5 font-medium text-green-800" title="The manager acts on this project">
                live{p.liveSource === 'settings' ? ' (deploy settings)' : ''}
              </span>
            ) : (
              <span className="rounded bg-slate-100 px-1.5 py-0.5 font-medium text-slate-700" title="The manager only records what it would do (Actions page)">
                shadow
              </span>
            )}
            {canEdit && !p.live && !confirmLive && (
              <Button variant="outline" size="sm" className="h-6 px-2 text-xs" onClick={() => setConfirmLive(true)}>
                <Power className="mr-1 h-3 w-3" /> Make live
              </Button>
            )}
            {canEdit && p.live && p.liveSource === 'app' && (
              <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => changeLive(false)} disabled={live.isPending} title="The manager stops acting here and only records again">
                Back to shadow
              </Button>
            )}
          </div>
          {confirmLive && (
            <div className="mt-2 max-w-sm space-y-2 rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">
              <p>
                The manager will act on this project: comments, labels and assignments, adding the bot users, starting AI work on
                issues assigned to the AI, reopening issues closed without evidence and nudging stalled ones. Then Check runs.
              </p>
              <div className="flex gap-2">
                <Button size="sm" className="h-6 px-2 text-xs" onClick={() => changeLive(true)} disabled={live.isPending || check.isPending}>
                  {live.isPending || check.isPending ? 'Setting up…' : 'Make live and set up'}
                </Button>
                <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => setConfirmLive(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
          {shown.notes && <div className="text-xs text-muted-foreground">{shown.notes}</div>}
          {error && <div className="text-xs text-red-600">{error}</div>}
        </td>
        <td className="py-2 pr-3">
          {canEdit && (proposal || !p.confirmedAt) ? (
            <Input value={base} onChange={(e) => setBase(e.target.value)} className="h-8 w-40 font-mono text-xs" />
          ) : (
            <span className="font-mono text-xs">{shown.baseBranch}</span>
          )}
        </td>
        <td className="py-2 pr-3 font-mono text-xs">{shown.companionProjectPath?.split('/').pop() || '—'}</td>
        <td className="py-2 pr-3 text-xs">
          {canEdit && (agentProfiles?.profiles.length || 0) > 1 ? (
            <select className="h-8 rounded border bg-background px-1 text-xs" value={agent} onChange={(e) => setAgent(e.target.value)}>
              <option value="">default ({agentProfiles?.default})</option>
              {agentProfiles!.profiles.map((ap) => (
                <option key={ap.name} value={ap.name}>
                  {ap.name}
                </option>
              ))}
            </select>
          ) : (
            <span className="font-mono">{p.agentProfile || agentProfiles?.default || '—'}</span>
          )}
        </td>
        <td className="py-2 pr-3 text-xs">
          {shown.reviewApps ? (
            <span className="font-mono">{shown.reviewAppUrl || shown.ciJobs.deployReview}</span>
          ) : shown.companionProjectPath ? (
            <span title="This project's branches are shown by the companion's review app (the MR with the same branch name)">
              via {shown.companionProjectPath.split('/').pop()}
            </span>
          ) : (
            'none'
          )}
          <div className="text-muted-foreground">main: {shown.ciJobs.deployMain}{shown.mainEnvUrl ? ` → ${shown.mainEnvUrl}` : ''}</div>
          {canEdit ? (
            <label className="mt-1 flex items-center gap-1" title="When the AI developer is done, the AI QA checks the work on the review app before you merge">
              <input type="checkbox" checked={qa} onChange={(e) => setQa(e.target.checked)} /> AI QA before merge
            </label>
          ) : (
            p.qa && <div>AI QA before merge</div>
          )}
          {canEdit ? (
            <label
              className={`mt-1 flex items-center gap-1 ${qa ? '' : 'opacity-50'}`}
              title="Before QA, a code review checks correctness, security and the logic against the acceptance criteria; a fail goes back to the developer"
            >
              <input type="checkbox" checked={qa && aiReview} disabled={!qa} onChange={(e) => setAiReview(e.target.checked)} /> AI code review before QA
            </label>
          ) : (
            p.aiReview && <div>AI code review before QA</div>
          )}
        </td>
        <td className="py-2 pr-3 text-xs">
          {shown.packages.length
            ? shown.packages.map((pkg) => (
                <div key={pkg.name}>
                  <span className="font-mono">{pkg.name}</span>
                  {pkg.dependents.length > 0 && <span className="text-muted-foreground"> → {pkg.dependents.map((d) => d.projectPath.split('/').pop()).join(', ')}</span>}
                </div>
              ))
            : '—'}
        </td>
        <td className="py-2 text-right">
          <div className="flex justify-end gap-1">
            {canCheck && (
              <Button variant="outline" size="sm" onClick={runCheck} disabled={check.isPending} title="Is it ready for AI management? Adds the missing CEO, PM and bot members">
                <ClipboardCheck className="mr-1 h-3 w-3" /> {check.isPending ? 'Checking…' : 'Check'}
              </Button>
            )}
            {canEdit && (
              <>
                <Button variant="outline" size="sm" onClick={run} disabled={discover.isPending} title="Read GitLab and propose values">
                  <Search className="h-3 w-3" />
                </Button>
                {(proposal || !p.confirmedAt || base !== p.baseBranch || agent !== (p.agentProfile || '') || qa !== !!p.qa || (qa && aiReview) !== !!p.aiReview) && (
                  <Button size="sm" onClick={confirm} disabled={save.isPending} title="Save and confirm">
                    <Check className="h-3 w-3" />
                  </Button>
                )}
              </>
            )}
          </div>
        </td>
      </tr>
      {report && (
        <tr className="border-b">
          <td colSpan={7} className="pb-3">
            <SetupResult report={report} />
          </td>
        </tr>
      )}
    </Fragment>
  );
}

function Releases() {
  const { data: releases = [], isLoading } = useReleases();
  const [pkg, setPkg] = useState('');
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Tag className="h-4 w-4" /> Releases
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <p className="text-muted-foreground">
          Releases are made by CI on the release branch (semantic-release) and dependency bumps by Renovate. The manager announces
          them, notes them on the released issues and records Renovate's bump MRs.
        </p>
        <form
          className="flex gap-2"
          onSubmit={async (e) => {
            e.preventDefault();
            if (!pkg.trim()) return;
            setBusy(true);
            try {
              setStatus(await releaseStatus(pkg.trim()));
            } catch (err) {
              setStatus((err as Error).message);
            } finally {
              setBusy(false);
            }
          }}
        >
          <Input value={pkg} onChange={(e) => setPkg(e.target.value)} placeholder="new-portal-model" className="w-64" />
          <Button type="submit" variant="outline" disabled={busy}>
            What is unreleased?
          </Button>
        </form>
        {status && <pre className="whitespace-pre-wrap rounded bg-muted p-3 text-xs">{status}</pre>}
        {isLoading ? (
          <LoadingSpinner />
        ) : releases.length === 0 ? (
          <p className="text-xs text-muted-foreground">No releases recorded yet.</p>
        ) : (
          <table className="w-full text-left text-xs">
            <tbody>
              {releases.map((r) => (
                <tr key={r._id} className="border-b">
                  <td className="py-1 pr-3 font-mono">{r.package || r.projectPath}</td>
                  <td className="py-1 pr-3 font-mono">{r.url ? <a href={r.url} target="_blank" rel="noreferrer" className="hover:underline">{r.tag}</a> : r.tag}</td>
                  <td className="py-1 pr-3">{formatRelativeTime(r.releasedAt)}</td>
                  <td className="py-1 pr-3">{r.issues.length ? r.issues.map((i) => `#${i}`).join(' ') : ''}</td>
                  <td className="py-1">
                    {r.bumpMrs.map((b) => (
                      <a key={`${b.projectPath}!${b.mrIid}`} href={b.url} target="_blank" rel="noreferrer" className="mr-2 font-mono hover:underline">
                        {b.projectPath.split('/').pop()}!{b.mrIid}
                      </a>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
}

/** The CEO adds a GitLab project by path or link; it appears below in shadow, ready for "Make live" */
function AddProject() {
  const { add } = useProfileActions();
  const [ref, setRef] = useState('');
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);

  return (
    <form
      className="flex flex-wrap items-center gap-2"
      onSubmit={async (e) => {
        e.preventDefault();
        if (!ref.trim()) return;
        setMessage(null);
        try {
          const p = await add.mutateAsync(ref.trim());
          setMessage({ ok: true, text: `${p.projectPath} ${p.created ? 'added' : 'is already here'}: press Make live on its row to set it up.` });
          setRef('');
        } catch (err) {
          setMessage({ ok: false, text: apiErrorMessage(err) });
        }
      }}
    >
      <Input
        value={ref}
        onChange={(e) => setRef(e.target.value)}
        placeholder="GitLab project path or link, e.g. royalvision-framework/newPortal"
        className="h-9 w-full max-w-md"
      />
      <Button type="submit" variant="outline" size="sm" disabled={add.isPending || !ref.trim()}>
        <Plus className="mr-1 h-3 w-3" /> {add.isPending ? 'Adding…' : 'Add a project'}
      </Button>
      {message && <span className={`text-xs ${message.ok ? 'text-green-700' : 'text-red-600'}`}>{message.text}</span>}
    </form>
  );
}

export default function ProjectSetup() {
  const { user } = useAuth();
  const { data: profiles = [], isLoading } = useProjectProfiles();
  const canEdit = user?.role === 'executive';
  const canCheck = user?.role === 'executive' || user?.role === 'pm';

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Project setup</h1>
        <p className="text-sm text-muted-foreground">
          Where work merges, how review apps and deployments run, and which packages each project publishes. The AI developer and
          the after-merge check use these values; confirm each project before it goes live. Check tells whether a project is
          ready for AI management and adds the members it needs: the CEO, the PM, the AI developers and the AI QA.
        </p>
      </div>
      <Card>
        <CardContent className="space-y-4 overflow-x-auto pt-4">
          {canEdit && <AddProject />}
          {isLoading ? (
            <LoadingSpinner />
          ) : (
            <table className="w-full text-left">
              <thead>
                <tr className="border-b text-xs text-muted-foreground">
                  <th className="pb-2 pr-3 font-medium">Project</th>
                  <th className="pb-2 pr-3 font-medium">Base branch</th>
                  <th className="pb-2 pr-3 font-medium">Companion</th>
                  <th className="pb-2 pr-3 font-medium">AI agent</th>
                  <th className="pb-2 pr-3 font-medium">Review apps / main</th>
                  <th className="pb-2 pr-3 font-medium">Packages</th>
                  <th className="pb-2" />
                </tr>
              </thead>
              <tbody>
                {profiles.map((p) => (
                  <ProfileRow key={p.gitlabProjectId} p={p} canEdit={canEdit} canCheck={canCheck} />
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
      <Releases />
    </div>
  );
}
